import { css } from 'lit';

// These page-specific styles stay together, like a page's own layout recipe.
export const homeStyles = css`
  :host {
    /* Custom elements are inline by default; block layout prevents a stray line-height gap. */
    display: block;
  }

  /* The hero is a stage: video behind, centered content in front. */
  #hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh; /* was 60vh */
    overflow: hidden;
    /* A dark fallback hides the gap if the video is late or unavailable. */
    background-color: #111;
    visibility: hidden;
  }

  /* Stretch the video to fill the stage, like a cover on a book. */
  #hero-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 0;
    visibility: hidden;
  }

  /* This translucent panel is a readable label placed over busy scenery. */
  #hero-overlay {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    padding: 16px 16px;
    max-width: 700px;
    margin: 0 auto;
    color: #fff;
    background: rgba(0, 0, 0, 0.45);
    border-radius: var(--wa-border-radius-l, 12px);
  }

  #hero-overlay wa-button::part(base) {
    background: #fff;
    color: #111;
  }

  #hero-overlay h1 {
    font-size: 2.25rem;
    font-weight: 800;
    margin: 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  }

  #hero-overlay .subtitle {
    font-size: 1.05rem;
    line-height: 1.6;
  }

  #cta-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-top: 12px;
  }

  /* Reduced-motion preference hides the animated video. */
  @media (prefers-reduced-motion: reduce) {
    #hero-video {
      display: none;
    }
  }

  /* Keep the intro easy to scan and sections aligned like pages in a book. */
  #business-info {
    max-width: 1000px;
    margin: 0 auto;
    padding: 10px 0 0 0;
  }
  wa-card{
     box-shadow: var(--wa-shadow-l, 0 8px 24px rgba(0, 0, 0, 0.15));
  }

  /* An accent bar and soft shadow lift the card off the page. */
  #business-info wa-card {
    display: block;
    text-align: center;
    border-top: 4px solid var(--wa-color-brand-fill-loud, #0b5fff);
    --spacing: 32px;
  }

  #business-info p {
    margin: 0 auto;
    max-width: 65ch;
    font-family: var(--wa-font-family-heading, sans-serif);
    font-size: clamp(1.1rem, 2vw, 1.4rem);
    font-weight: 600;
    line-height: 1.7;
  }
  #features,
  #security,
  #accessibility,
  #apps-script {
    max-width: 1000px;
    margin: 0 auto;
    padding: 16px 16px;
  }

  #features h2,
  #security h2,
  #accessibility h2,
  #apps-script h2,
  #parallax h2 {
    text-align: center;
    margin-bottom: 8px;
  }

  .section-intro {
    text-align: center;
    color: var(--wa-color-text-quiet, #666);
    max-width: 640px;
    margin: 0 auto 24px;
  }

  /* Cards form as many columns as fit, like tiles filling a shelf. */
  #features-grid,
  #accessibility-grid,
  #apps-script-grid,
  #parallax-grid,
  #security-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
  }



  /* This viewport is a window that clips the horizontally moving security cards. */
  #security-gallery-viewport {
    overflow: hidden;
  }

  #parallax {
    max-width: 1400px;
   // width: 100%;
    margin: 0 auto;
    padding: 16px;
  }

  /* The frame clips the oversized image so only a window of it is visible. */
  #parallax-frame {
    position: relative;
    overflow: hidden;
    min-height: 60vh; /* was 60vh */
    display: grid;
    place-items: center;
    border-radius: var(--wa-border-radius-l, 12px);
  }

  /* Oversized background gives the parallax image room to drift without exposing edges. */
  #parallax-bg {
    position: absolute;
    top: -20%;
    left: 0;
    width: 100%;
    height: 140%;
    z-index: 0;
    will-change: transform;
  }

  #parallax-bg img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Foreground text sits above the image, like a caption on a photo. */
  #parallax-text {
    position: relative;
    z-index: 1;
    padding: 16px 24px;
    text-align: center;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    border-radius: var(--wa-border-radius-l, 12px);
  }

  #parallax-text h1 {
    margin: 0;
  }

  #parallax .section-intro {
    max-width: 1000px;
    margin-inline: auto;
  }

  /* Center each card's icon and copy for quick scanning. */
  .feature-card,
  .a11y-card,
  .apps-script-card {
    text-align: center;
  }

  .feature-card wa-icon,
  .a11y-card wa-icon,
  .apps-script-card wa-icon {
    font-size: 32px;
    color: var(--app-color-primary);
  }

  .feature-card h3,
  .security-card h3,
  .a11y-card h3,
  .apps-script-card h3 {
    margin: 8px 0 4px;
  }

  .security-card code,
  .a11y-card code,
  .apps-script-card code {
    background: var(--wa-color-surface-lowered, rgba(0, 0, 0, 0.06));
    padding: 0 4px;
    border-radius: 4px;
    font-size: 0.9em;
  }

  @media (min-width: 750px) {
    #hero-overlay h1 {
      font-size: 2.75rem;
    }

    #security {
      max-width: none;
    }

    #security h2,
    #security .section-intro {
      max-width: 1000px;
      margin-inline: auto;
    }

    #security-gallery-viewport {
      max-width: 1000px;
      margin-inline: auto;
    }

    /* At desktop width, cards become one long strip for the scroll animation. */
    #security-grid {
      display: flex;
      width: max-content;
      gap: 24px;
    }

    .security-card {
      flex: 0 0 clamp(18rem, 30vw, 24rem);
      min-height: 19rem;
    }

  }
`;
